import { useState, forwardRef, useImperativeHandle, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Button } from "react-bootstrap";
import { showError } from "../utility/ui/notifications/Toaster";

import { getApprovedPendingRepairWorkflow, getProjectReqCount, getallApprovedCount } from "../../Services/weldingRepairAnalysis-service";
import { getCurrentDateTime } from "../../functions/welding-functions";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import GridMaster from "../utility/grid/GridMaster";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import { getWIReqCount } from "../../Services/WI-services";
import Chart from "react-apexcharts";
import Export from "../../icons/Download_Blue_Big.svg";
import WhiteExport from "../../icons/Download_White.svg";
import { handleExport1 } from "../utility/data/export/GlobalExport";

/**
 * WELDING REPAIR ANALYSIS DASHBOARD
 * 
 * This component displays 4-5 main grids/charts for welding repair analysis:
 * 
 * 1. PROJECT REPAIR STATISTICS GRID (Main Grid) fetchProjectReqCount
 *    - Shows project-wise repair counts and first attempt clear percentages
 *    - Columns: Project Number, Repair Count, % First Attempt Clear
 *    - Data Sources: 
 *      • getProjectReqCount() - Gets project-wise repair counts and aggregates by project
 *      • getWIReqCount() - Gets WI (Welding Instruction) request counts per project for percentage calculation
 *    - Purpose: Track repair performance by project and correlate with WI activity
 *    - Logic: Calculates % First Attempt Clear = ((WI Total - Repair Count) / WI Total) * 100
 * 
 * 2. PENDING APPROVALS GRID (Received for Approval) fetchApprovedPendingRepair
 *    - Shows repair reports pending approval for current user
 *    - Columns: Status, Report Number, Ageing (Days)
 *    - Data Source: getApprovedPendingRepairWorkflow() filtered by user role
 *    - Purpose: Track items waiting for user's approval
 * 
 * 3. RAISED REPORTS GRID (Raised for Approval) fetchAllRepairWorkflowByUserIdWise
 *    - Shows repair reports raised by current user pending with others
 *    - Columns: Status, Report Number, Pending with, Ageing (Days)
 *    - Data Source: getApprovedPendingRepairWorkflow() filtered by user ID
 *    - Purpose: Track user's submitted reports awaiting approval
 * 
 * 4. MONTHLY TREND CHART (Line Chart)
 *    - Shows monthly repair trends over selected year
 *    - X-axis: Months (Apr-Mar financial year)
 *    - Y-axis: Enquiry (Value and Volume)
 *    - Data Source: totalApprovedCountGraph state
 *    - Purpose: Visualize repair trends over time
 *    - getallApprovedCount
 * 
 * 5. YEAR SELECTOR DROPDOWN
 *    - Allows filtering data by financial year
 *    - Range: Configurable start year + 20 years
 *    - Purpose: Historical data analysis
 * 
 * ROLE-BASED ACCESS:
 * - Only Welding Engineers (WE) can raise new repair reports
 * - Different users see different pending approvals based on role
 * - Ageing calculation shows days since report initiation
 */
const WeldingRepairAnalysis = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const childRef = useRef();

  // User data
  const userId = sessionStorage.getItem("UserId")?.split("@")[0];
  const userRole = sessionStorage.getItem("Role");
  const todayDate = getCurrentDateTime("-");
  const currentYear = new Date().getFullYear();

  // State management
  const [allPendingRepairAnalysis, setAllPendingRepairAnalysis] = useState([]);
  const [allRaisedRepairAnalysis, setAllRaisedRepairAnalysis] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [, setExcelData] = useState([]);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight - 37);
  const [columnReceivedforApproval, setColumnReceivedforApproval] = useState([]);
  const [columnRaisedforApproval, setColumnRaisedforApproval] = useState([]);
  const [reqCount, setReqCount] = useState([]);
  const [totalApprovedCountGraph, setTotalApprovedCountGraph] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  /**
   * GRID 1: PROJECT REPAIR STATISTICS - Main performance dashboard
   * Shows overall repair performance metrics by project
   */
  const tmpcolumnDefs = useMemo(() => [
    {
      headerName: "Project Number",
      field: "projectNo",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
      sortable: true,
      sort: "asc",
    },
    {
      headerName: "Repair Count",          // Total number of repairs for project
      field: "ApprovedCount",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
      minWidth: 50,
    },
    {
      headerName: "% First Attempt Clear",  // Success rate metric
      field: "percentage",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
    },
  ], []);

  /**
   * GRID 2: PENDING APPROVALS - Items waiting for current user's approval
   * Shows repair reports that need the current user's attention
   */
  const tableReceivedforApproval = useMemo(() => [
    {
      headerName: "Status",
      field: "repairAnalysisStatus",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "WeldRepairRenderer",   // Custom status renderer
      sortable: true,
    },
    {
      headerName: "Report Number",
      field: "reportNumber",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "HyperLink",            // Clickable link to report
      minWidth: 50,
    },
    {
      headerName: "Ageing (Days)",          // Days since report creation
      field: "aging(days)",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
      sort: "desc",                         // Show oldest first
    },
  ], []);

  /**
   * GRID 3: RAISED REPORTS - Items raised by current user pending with others
   * Shows status of reports submitted by current user
   */
  const tableRaisedforApproval = useMemo(() => [
    {
      headerName: "Status",
      field: "repairAnalysisStatus",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "WeldRepairRenderer",
      sortable: true,
    },
    {
      headerName: "Report Number",
      field: "reportNumber",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "HyperLink",
      minWidth: 50,
    },
    {
      headerName: "Pending with",           // Shows who needs to approve
      field: "approvalPendingWith",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
      minWidth: 50,
    },
    {
      headerName: "Ageing (Days)",
      field: "aging(days)",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "",
      sort: "desc",
    },
  ], []);

  /**
   * CHART 4: MONTHLY TREND LINE CHART
   * Displays repair trends over financial year (Apr-Mar)
   * Uses ApexCharts library for interactive visualization
   */
  const chartOptions = useMemo(() => ({
    chart: {
      id: "straight-line-chart",
      toolbar: { show: false },              // Hide chart toolbar
    },
    xaxis: {
      // Financial year months (Apr to Mar)
      categories: ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"],
      title: {
        text: "Months",
        style: { fontWeight: "normal" },
      },
    },
    yaxis: {
      title: {
        text: "Enquiry (Value and Volume)",   // Repair count/volume
        style: { fontWeight: "normal" },
      },
    },
    colors: ["var(--godrej-green-alt)"],      // Company brand color
    dataLabels: { enabled: false },           // Hide data point labels
    stroke: { width: 3 },                     // Line thickness
    grid: {
      borderColor: "var(--col-ddd)",
      borderWidth: 1,
      zindex: 10,
    },
  }), []);

  // Callbacks
  const handleYearChange = useCallback((e) => {
    setSelectedYear(e.value);
  }, []);

  const HyperLink = useCallback((params) => (
    <Button
      variant="link"
      onClick={() => {
        navigate("/weldingRepair/WeldingRepairAnalysisEntry", {
          state: {
            data: params.data,
            action: "Edit",
          },
        });
      }}
      size="sm"
    >
      {params.data.reportNumber}
    </Button>
  ), [navigate]);

  const headerName = useCallback((field) =>
    getHeader_FieldName(field, tmpcolumnDefs, "getHeader"), [tmpcolumnDefs]);

  const fieldName = useCallback((header) =>
    getHeader_FieldName(header, tmpcolumnDefs, "getField"), [tmpcolumnDefs]);

  const headerReceivedforApproval = useCallback((field) =>
    getHeader_FieldName(field, tableReceivedforApproval, "getHeader"), [tableReceivedforApproval]);

  const fieldReceivedforApproval = useCallback((header) =>
    getHeader_FieldName(header, tableReceivedforApproval, "getField"), [tableReceivedforApproval]);

  const headerRaisedforApproval = useCallback((field) =>
    getHeader_FieldName(field, tableRaisedforApproval, "getHeader"), [tableRaisedforApproval]);

  const fieldRaisedforApproval = useCallback((header) =>
    getHeader_FieldName(header, tableRaisedforApproval, "getField"), [tableRaisedforApproval]);

  const handleClick = useCallback(() => {
    if (userRole !== "WE") {
      showError(`Only welding engineer is allowed to raise weld repair.`);
    } else {
      navigate("/weldingRepair/WeldingRepairAnalysisEntry", {
        state: { data: "", action: "Add" },
      });
    }
  }, [userRole, navigate]);

  const Graphyears = useCallback(() => {
    const startYear = parseInt(process.env.REACT_APP_START_YEAR);
    return Array.from(new Array(20), (_, index) => index + startYear)
      .map(year => <option key={year} value={year}>{year}</option>);
  }, []);

  // Data fetching functions
  const fetchColumnDefns = useCallback(async () => {
    const result = GlobalColumnDefinations(tmpcolumnDefs);
    if (result) setColumnDefs(result);
  }, [tmpcolumnDefs]);

  const fetchColumnReceivedforApproval = useCallback(async () => {
    const result = GlobalColumnDefinations(tableReceivedforApproval);
    result[1].cellRenderer = HyperLink;
    setColumnReceivedforApproval(result);
  }, [tableReceivedforApproval, HyperLink]);

  const fetchColumnRaisedforApproval = useCallback(async () => {
    const result = GlobalColumnDefinations(tableRaisedforApproval);
    result[1].cellRenderer = HyperLink;
    setColumnRaisedforApproval(result);
  }, [tableRaisedforApproval, HyperLink]);

  /**
   * DATA FETCHER: PENDING APPROVALS GRID (Grid 2)
   * Fetches repair reports pending approval for current user
   * 
   * LOGIC:
   * 1. Gets all repair workflows by user role
   * 2. Calculates ageing in days from initiation date
   * 3. Filters to show only items pending with current user's role
   * 4. Populates "Received for Approval" grid
   */
  const fetchApprovedPendingRepair = useCallback(async () => {
    const yearData = new Date().getFullYear();
    const userObj = {
      userId,
      userRole,
      flag: "AllUserRoleWise",              // Get by role-based filtering
      year: yearData,
    };

    const repairResult = await getApprovedPendingRepairWorkflow(userObj);
    if (!repairResult?.length) return;

    const date2 = new Date(todayDate);
    const filtered = repairResult
      .map(item => {
        // Calculate ageing in days
        const date1 = new Date(item.initiatedDate);
        const timeDifference = date2.getTime() - date1.getTime();
        return {
          ...item,
          "aging(days)": Math.round(timeDifference / (1000 * 60 * 60 * 24))
        };
      })
      .filter(item => item.approvalPendingWith === userRole);  // Only items for current user

    setAllPendingRepairAnalysis(filtered);
  }, [userId, userRole, todayDate]);

  /**
   * DATA FETCHER: RAISED REPORTS GRID (Grid 3)
   * Fetches repair reports raised by current user pending with others
   * 
   * LOGIC:
   * 1. Gets all repair workflows by user ID
   * 2. Calculates ageing in days from initiation date
   * 3. Shows status of user's submitted reports
   * 4. Populates "Raised for Approval" grid
   */
  const fetchAllRepairWorkflowByUserIdWise = useCallback(async () => {
    const yearData = new Date().getFullYear();
    const userObj = {
      userId,
      userRole,
      flag: "AllUserIdWise",               // Get by user ID filtering
      year: yearData,
    };

    const repairResult = await getApprovedPendingRepairWorkflow(userObj);
    if (!repairResult?.length) return;

    const date2 = new Date(todayDate);
    const filtered = repairResult
      .map(item => {
        // Calculate ageing in days
        const date1 = new Date(item.initiatedDate);
        const timeDifference = date2.getTime() - date1.getTime();
        return {
          ...item,
          "aging(days)": Math.round(timeDifference / (1000 * 60 * 60 * 24))
        };
      })
      .filter(item => item.createdBy === userId);

    setAllRaisedRepairAnalysis(filtered);
  }, [userId, userRole, todayDate]);

  /**
   * DATA FETCHER: PROJECT REPAIR STATISTICS GRID (Grid 1)
   * Populates the main project-wise repair performance dashboard
   * 
   * DATA SOURCES:
   * - getProjectReqCount(): Gets repair counts by project (ApprovedCount, RaisedCount)
   * - getWIReqCount(): Gets total WI requests per project for success rate calculation
   * 
   * PROCESSING LOGIC:
   * 1. Aggregates repair data by project (handles multiple entries per project)
   * 2. Splits composite key (SK) format: "ProjectNo#Year#Month"
   * 3. Sums ApprovedCount, RaisedCount, and count across all time periods per project
   * 4. Calculates % First Attempt Clear = ((WI Total - Repair Count) / WI Total) * 100
   * 5. Higher percentage = better performance (fewer repairs needed)
   * 
   * OUTPUT FIELDS:
   * - projectNo: Project identifier
   * - ApprovedCount: Total approved repairs for project
   * - count: Total WI requests (converted to string)
   * - percentage: Success rate formatted as "XX%"
   */
  const fetchProjectReqCount = useCallback(async () => {
    const projectCount = await getProjectReqCount();

    // Aggregate projects by projectNo (handle multiple time period entries)
    const aggregatedProjects = projectCount.reduce((data, { SK, ApprovedCount = 0, RaisedCount = 0, count = 0 }) => {
      const [projectNo, year, month] = SK.split("#");  // Parse composite key

      data[projectNo] = data[projectNo] || {
        SK,
        projectNo,
        year,
        month,
        ApprovedCount: 0,
        RaisedCount: 0,
        count: 0,
      };

      // Sum values across all time periods for each project
      data[projectNo].ApprovedCount += ApprovedCount;
      data[projectNo].RaisedCount += RaisedCount;
      data[projectNo].count += count;

      return data;
    }, {});

    // Calculate success percentages using WI request counts
    const finalCount = await Promise.all(
      Object.values(aggregatedProjects).map(async (project) => {
        const total = await getWIReqCount(project.projectNo);  // Get total WI requests
        const percentage = total
          ? Math.round(((total - project.ApprovedCount) / total) * 100)  // Success rate calculation
          : 100;  // Default to 100% if no WI data

        return {
          ...project,
          count: total.toString(),
          percentage: `${percentage}%`,
        };
      })
    );

    setReqCount(finalCount);
    fetchColumnDefns();
  }, [fetchColumnDefns]);

  /**
   * DATA FETCHER: MONTHLY TREND CHART (Chart 4)
   * Populates the line chart showing monthly repair trends
   * 
   * DATA SOURCE:
   * - getallApprovedCount(selectedYear): Gets monthly approved repair counts for selected year
   * 
   * PROCESSING LOGIC:
   * 1. Fetches repair data aggregated by month for the selected financial year
   * 2. Data structure expected: { totalApprovedCount: [{ value: number }, ...] }
   * 3. Array corresponds to 12 months (Apr-Mar financial year)
   * 4. Updates chart data when selectedYear changes
   * 
   * CHART INTEGRATION:
   * - Data flows to graphData useMemo which extracts values for ApexCharts
   * - X-axis: Fixed months ["Apr", "May", "Jun", ..., "Mar"]
   * - Y-axis: Dynamic values from totalApprovedCount array
   * - Allows year-over-year trend analysis via dropdown selector
   */
  const getTotalApprovedCount = useCallback(async () => {
    const totalApprovedCount = await getallApprovedCount(selectedYear);
    setTotalApprovedCountGraph(totalApprovedCount);
  }, [selectedYear]);

  const handleGlobalExport = useCallback(() => {
    const exportColumns = [
      { headerName: "Status", field: "repairAnalysisStatus" },
      { headerName: "Report Number", field: "reportNumber" },
      { headerName: "Created On", field: "createdOn" },
      { headerName: "Project Number", field: "projectNumber" },
      { headerName: "Client", field: "client" },
      { headerName: "Equipment Number", field: "equipmentNumber" },
      { headerName: "Inspection By", field: "inspectionBy" },
      { headerName: "Drawing Number", field: "drawingNumber" },
      { headerName: "Sheet Number", field: "sheetNumber" },
      { headerName: "Revision Number", field: "revisionNumber" },
      { headerName: "Originating Dept", field: "orginatingDept" },
      { headerName: "Joint Number", field: "jointNumber" },
      { headerName: "Spot Number", field: "spotNumber" },
      { headerName: "NDT Method", field: "ndtMethod" },
      { headerName: "Material", field: "material" },
      { headerName: "Welding Process", field: "weldingProcess" },
      { headerName: "Welding Code", field: "welderNumber" },
      { headerName: "Welding Name", field: "welderName" },
      { headerName: "Repair OR Defect Type", field: "repairOrDefectType" },
      { headerName: "Repair Location Marked By", field: "repairLocationMarkedBy" },
      { headerName: "Repair Length Spotwise", field: "repairLengthSpotwise" },
      { headerName: "Repair Legth Actual Wise", field: "repairLengthActualwise" },
      { headerName: "Repair Removed By", field: "repairRemovedByAfterRepairRemoval" },
      { headerName: "Repair Found", field: "repairFoundAfterRepairRemoval" },
      { headerName: "Repair Depth", field: "repairDepthAfterRepairRemoval" },
      { headerName: "Defective Process", field: "defectiveProcessAfterRepairRemoval" },
      { headerName: "Welding Joint Detail", field: "weldingJointDetailAfterRepairRemoval" },
      { headerName: "Why-1", field: "rootCauseAnalysisWhy1" },
      { headerName: "Why-2", field: "rootCauseAnalysisWhy2" },
      { headerName: "Why-3", field: "rootCauseAnalysisWhy3" },
      { headerName: "Why-4", field: "rootCauseAnalysisWhy4" },
      { headerName: "Why-5", field: "rootCauseAnalysisWhy5" },
      { headerName: "Others", field: "rootCauseAnalysisOthers" },
      { headerName: "Root Cause", field: "rootCauseAnalysisRootCause" },
      { headerName: "Corrective Action", field: "rootCauseAnalysisCorrectiveAction" },
      { headerName: "Preventive Action", field: "rootCauseAnalysisPreventionAction" },
      { headerName: "Kaizen Idea", field: "rootCauseAnalysisKaizenIdea" },
      { headerName: "Counter Measure", field: "rootCauseAnalysisCounterMeasure" },
      { headerName: "Cost of Repair", field: "costOfRepair" },
    ];

    const modifiedData = allRaisedRepairAnalysis.map(row => ({
      ...row,
      welderNumber: row.welderNumber ? `'${row.welderNumber.toString()}'` : "",
    }));

    handleExport1(modifiedData, "Weld Repair Analysis", exportColumns);
  }, [allRaisedRepairAnalysis]);

  // Effects
  useEffect(() => {
    fetchApprovedPendingRepair();
    fetchAllRepairWorkflowByUserIdWise();
    fetchProjectReqCount();
    fetchColumnReceivedforApproval();
    fetchColumnRaisedforApproval();
  }, [
    fetchApprovedPendingRepair,
    fetchAllRepairWorkflowByUserIdWise,
    fetchProjectReqCount,
    fetchColumnReceivedforApproval,
    fetchColumnRaisedforApproval
  ]);

  useEffect(() => {
    getTotalApprovedCount();
  }, [getTotalApprovedCount]);

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight - 37);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useImperativeHandle(ref, () => ({
    handleAdd: handleClick
  }));

  // Chart data
  const graphData = useMemo(() => {
    const chartData = totalApprovedCountGraph?.totalApprovedCount;
    return chartData && chartData.map(entry => entry.value);
  }, [totalApprovedCountGraph]);

  // Render authorized content or unauthorized message
  const isAuthorized = ["WE", "Supervisor", "QCE", "NDE"].includes(userRole);

  if (!isAuthorized) {
    return (
      <Row className="RowHeight">
        <Col
          md={2}
          sm={12}
          xs={12}
          className="ListTitle"
          style={{ position: "relative", top: "200px", left: "400px" }}
        >
          You are not authorized to enter / view weld repair data
        </Col>
      </Row>
    );
  }

  return (
    <div
      className="hide_Container"
      style={{
        height: viewportHeight,
        overflow: "auto",
        paddingBottom: "100px",
        overflowY: "scroll",
        overflowX: "hidden",
        position: "relative",
        bottom: "10px",
      }}
    >
      <div style={{ position: "relative", top: "38px", left: "10px" }}>
        <button
          onClick={handleGlobalExport}
          type="button"
          className="btn btn-outline-primary hover_btttn"
          style={{
            background: "#F4F4FF",
            border: "none",
            fontSize: "14px",
            height: "35px", width: "150px"
          }}>
          <img className="default_img" src={Export} alt="Export" />
          <img className="hover_img" src={WhiteExport} alt="White Export" />
          <span style={{ position: "relative", bottom: "24px", left: "10px" }}>
            Download
          </span>
        </button>
      </div>
      <div className="weldrepairtitle">
        <div className="repairtitleleft">
          <span style={{ fontSize: "18px" }}>Project Wise Repair</span>
        </div>
        <div className="repairtitleright">
          <span style={{ fontSize: "18px" }}>Monthly Repair</span>
        </div>
      </div>

      <div className="weldrepairview" style={{ zIndex: "10" }}>
        <div className="repairviewleft">
          <Button
            variant="link"
            className="label-color"
            onClick={() => {
              navigate("/weldingRepair/WeldingRepairAnalysisViewAll", {
                state: { data: "", action: "viewReqCount" },
              });
            }}
            size="sm"
          >
            View All
          </Button>
        </div>
        <div className="repairviewright">
          <Button
            variant="link"
            onClick={() => {
              navigate("/weldingRepair/WeldingRepairAnalysisViewAll", {
                state: { data: "", action: "ViewReceivedforapproval" },
              });
            }}
            size="sm"
          />
        </div>
      </div>

      <div className="weldrepairtitlebottom" style={{ zIndex: "-1" }}>
        <div className="repairtitleleft">
          <span style={{ fontSize: "18px" }}>
            List of workflow received for approval
          </span>
        </div>
        <div className="repairtitleright">
          <span style={{ fontSize: "18px" }}>
            List of workflow raised by logged in user for approval
          </span>
        </div>
      </div>

      <div className="weldrepairviewbottom">
        <div className="repairviewleftbottom">
          <Button
            className="label-color"
            variant="link"
            onClick={() => {
              navigate("/weldingRepair/WeldingRepairAnalysisViewAll", {
                state: { data: "", action: "ViewReceivedforapproval" },
              });
            }}
            size="sm"
          >
            View All
          </Button>
        </div>
        <div className="repairviewrightbottom">
          <Button
            className="label-color"
            variant="link"
            onClick={() => {
              navigate("/weldingRepair/WeldingRepairAnalysisViewAll", {
                state: { data: "", action: "ViewRaisedforapproval" },
              });
            }}
            size="sm"
          >
            View All
          </Button>
        </div>
      </div>

      <div className="grid-container">
        <div className="repairgridtableleft">
          <GridMaster
            rowDetails={reqCount}
            colDetails={columnDefs}
            fieldNames={fieldName}
            headerNames={headerName}
            setExcelData={setExcelData}
            ref={childRef}
            paginationPageSize={4}
            pagination={false}
            height={210}
            width={100}
            rowHeight={40}
            headerHeight={40}
          />
        </div>

        <div className="repairgridtableright">
          <div className="repairgraphtoplabel">
            <div className="weldrepairtitledash"></div>
            Enquiry Volume (In Nos.) year{" "}
            <select
              className="weldgraphyeardropdown"
              value={selectedYear}
              onChange={(opt) => handleYearChange(opt.target)}
            >
              {Graphyears()}
            </select>
          </div>
          <div style={{ width: "90%", paddingLeft: "20px" }}>
            <Chart
              options={chartOptions}
              series={[{ data: graphData }]}
              type="line"
              height={175}
            />
          </div>
        </div>

        <div className="repairgridtableleftbottom" style={{ zIndex: "10" }}>
          <GridMaster
            rowDetails={allPendingRepairAnalysis}
            colDetails={columnReceivedforApproval}
            fieldNames={fieldReceivedforApproval}
            headerNames={headerReceivedforApproval}
            setExcelData={setExcelData}
            ref={childRef}
            paginationPageSize={4}
            pagination={false}
            height={230}
            width={100}
            rowHeight={43}
            headerHeight={43}
          />
        </div>

        <div className="repairgridtablerightbottom" style={{ zIndex: "10" }}>
          <GridMaster
            rowDetails={allRaisedRepairAnalysis}
            colDetails={columnRaisedforApproval}
            fieldNames={fieldRaisedforApproval}
            headerNames={headerRaisedforApproval}
            setExcelData={setExcelData}
            ref={childRef}
            paginationPageSize={4}
            pagination={false}
            height={230}
            width={100}
            rowHeight={43}
            headerHeight={43}
          />
        </div>
      </div>


    </div>
  );
});

export default WeldingRepairAnalysis;
